import { describeAncestry, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import {
  col,
  ESP_COMPONENTS,
  ESP_DEPTHS,
  ESP_INSTALLATION,
  ESP_SURFACE,
  IDENTITY,
  type Shape,
} from '../shapes/index.ts';
import { makeStringContextViews } from './makeStringContextViews.ts';
import { EspPumpDetailsView } from '#schema/generated/ipso/views.ts';

/**
 * Identity + the producing-string relation for the ESP completion view. Unlike
 * the standard {@link STRING_KEEP}, the `ESP_Pump_Details` source spells its
 * string relation `entityName` (→ `String_asset`), so it is renamed to the
 * canonical `wellString` every per-string view exposes — which is also the ref
 * `STRING_CONTEXT_ENRICH` resolves well/field/reservoir ancestry off. The
 * source's own direct `field` relation is intentionally dropped: the enrichment
 * supplies a richer `{ space, externalId, name }` `field` struct, and keeping
 * both would collide on the column name.
 */
const ESP_DETAILS_KEEP: Shape = [
  ...IDENTITY,
  col('wellString', 'unitless', 'Producing string (completion) this ESP installation belongs to.', {
    suffix: 'entityName',
  }),
];

/**
 * `EspCompletionDetails` — one consolidated, ancestry-enriched surface over the
 * installed-ESP nameplate record (`ESP_Pump_Details`, WELL-STRING grain). The
 * ~70 source columns are de-prefixed into four groups: installation/lifecycle
 * scalars, setting depths, the per-component downhole equipment tally (each
 * component a struct), and the surface power gear. Filter by `wellString` (or
 * join WellEntity) to scope by hierarchy.
 */
export const useEspDetailsViews = makeStringContextViews({
  source: () => EspPumpDetailsView,
  keep: ESP_DETAILS_KEEP,
  specs: [
    {
      name: 'EspCompletionDetails',
      description: `Installed ESP completion nameplate + lifecycle per well string: install/run/pull dates, status, pull reason and root cause; setting depths; the downhole equipment tally (pump, motor, protector, intake, AGH, BOH, MLE, cable, monitor) as per-component structs; and the surface power gear (VSD panel, transformer)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
      groups: [{ shape: ESP_INSTALLATION }, { shape: ESP_DEPTHS }, { shape: ESP_COMPONENTS }, { shape: ESP_SURFACE }],
    },
  ],
});
